import { type ComponentFn } from '@littlebird/action-cards-view/react';
import { Children, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { Markdown } from '@/components/ui/smoothdown';
import { cn } from '@/lib/utils';

import { useElementId } from '../fieldIds';
import { Avatar } from '../internals/Avatar';
import { ClampedText } from '../internals/ClampedText';
import { useHostContextPeek } from '../internals/hostContextPeek';
import { formatFullDateTime, formatMessageTime } from '../internals/localTime';
import { MrkdwnText } from '../internals/MrkdwnText';
import { useCardMetrics } from '../internals/useCardMetrics';
import { useTimeFormat } from '../internals/useTimeFormat';
import { MARKDOWN_CLASS, sizingClass, TEXT_STYLE } from '../tokens';

import { TextArea } from './TextArea';

import type { catalog } from '@littlebird/action-cards-view';

type MessageFormat = 'plain' | 'mrkdwn' | 'markdown';

function ReadOnlyText({
  id,
  text,
  format,
  lines,
  quoted,
}: {
  id: string;
  text: string;
  format: MessageFormat;
  lines: number | undefined;
  quoted: boolean;
}) {
  const toneClass = quoted ? 'text-foreground-subtle' : 'text-foreground';
  if (format === 'markdown') {
    return (
      <ClampedText lines={lines}>
        <Markdown
          id={id}
          content={text}
          className={cn(MARKDOWN_CLASS, toneClass)}
        />
      </ClampedText>
    );
  }
  return (
    <ClampedText
      lines={lines}
      className={cn(
        TEXT_STYLE.body,
        toneClass,
        'whitespace-pre-wrap break-words'
      )}
    >
      {format === 'mrkdwn' ? <MrkdwnText text={text} /> : text}
    </ClampedText>
  );
}

// The element Gmail serves for the email a reply answers (gmail/views.py)
const REPLIED_TO_SUFFIX = '-replied_to';

// A message row in the source app's anatomy at the card's scale: the avatar spans
// the name line and the first text line. A bound `text` is the user's own message,
// edited in place through TextArea
export const Message: ComponentFn<typeof catalog, 'Message'> = ({
  props,
  bindings,
  emit,
  on,
  children,
}) => {
  const id = useElementId(props);
  const metrics = useCardMetrics();
  const timeline = props.appearance === 'timeline';
  const avatarSize = metrics.mobile ? 28 : 24;
  const textPath = bindings?.text;
  const format = props.format ?? 'plain';
  const prefer12Hr = useTimeFormat();
  // The email a reply answers moves into the host's context line, where one is set. Other timeline messages, such
  // as the Notion comment a card deletes, stay in the card
  const contextPeek = useHostContextPeek();
  const peeked =
    contextPeek !== null &&
    timeline &&
    textPath === undefined &&
    id.endsWith(REPLIED_TO_SUFFIX);
  const registerSource = contextPeek?.registerSource;
  const authorName = props.authorName;
  const sourceText = props.text;
  useEffect(
    () =>
      peeked && registerSource
        ? registerSource({ author: authorName, text: sourceText })
        : undefined,
    [peeked, registerSource, authorName, sourceText]
  );

  const text =
    textPath !== undefined ? (
      <TextArea
        props={{
          ...props,
          value: props.text,
          appearance: 'inline',
          textStyle: 'body',
          bodyClass: TEXT_STYLE.body,
          ariaLabel: `Message from ${props.authorName}`,
        }}
        bindings={{ value: textPath }}
        emit={emit}
        on={on}
      />
    ) : (
      <ReadOnlyText
        id={id}
        text={props.text}
        format={format}
        lines={props.lines}
        quoted={props.tone === 'quoted'}
      />
    );

  const message = (
    <div
      className={cn(
        'grid min-w-0 gap-x-[var(--ac-gap-sm)]',
        timeline &&
          'overflow-hidden rounded-[calc(8*var(--ac-u))] border border-border',
        sizingClass(props.sizing)
      )}
      style={{ gridTemplateColumns: `${avatarSize}px minmax(0,1fr)` }}
    >
      <Avatar
        id={props.authorId ?? props.authorName}
        name={props.authorName}
        avatarUrl={props.avatarUrl}
        shape={props.avatarShape ?? 'rounded'}
        size={avatarSize}
        title={props.authorDetail}
        className={cn(
          timeline
            ? 'ml-[calc(12*var(--ac-u))] mt-[var(--ac-gap-md)]'
            : 'row-span-2 self-start'
        )}
      />
      <div
        className={cn(
          'flex min-w-0 items-baseline gap-[calc(6*var(--ac-u))]',
          timeline && 'px-[calc(12*var(--ac-u))] py-[var(--ac-gap-md)]'
        )}
      >
        <span
          title={props.authorDetail}
          className="truncate text-[length:var(--ac-note)] font-semibold leading-[var(--ac-meta-lh)] text-foreground"
        >
          {props.authorName}
        </span>
        {props.at ? (
          <time
            dateTime={props.at}
            title={formatFullDateTime(props.at, prefer12Hr)}
            className={cn(
              'shrink-0 whitespace-nowrap text-foreground-muted',
              TEXT_STYLE.meta
            )}
          >
            {formatMessageTime(props.at, prefer12Hr)}
          </time>
        ) : null}
        {props.meta ? (
          <span
            className={cn('shrink-0 text-foreground-muted', TEXT_STYLE.meta)}
          >
            {props.meta}
          </span>
        ) : null}
      </div>
      <div
        className={cn(
          'relative min-w-0',
          timeline
            ? 'col-span-2 border-t border-border-subtle px-[calc(12*var(--ac-u))] py-[var(--ac-gap-md)]'
            : 'col-start-2'
        )}
      >
        {text}
      </div>
      {Children.count(children) > 0 ? (
        <div className="col-start-2 mt-[calc(6*var(--ac-u))] flex min-w-0 flex-wrap gap-[calc(6*var(--ac-u))]">
          {children}
        </div>
      ) : null}
    </div>
  );
  if (!peeked) return message;
  return contextPeek?.sourceTarget
    ? createPortal(message, contextPeek.sourceTarget)
    : null;
};
